// src/views/AdminUserTasks.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Breadcrumb,
  Card as BootstrapCard,
} from "react-bootstrap";
import {
  Table,
  Tag,
  Button,
  Select,
  Input,
  Space,
  Empty,
  Spin,
  message,
  Pagination,
  Card,
  Statistic,
  Tooltip,
  Badge,
  Modal,
  Descriptions,
  Timeline,
  Divider,
  Alert,
} from "antd";
import {
  HomeOutlined,
  UserOutlined,
  SearchOutlined,
  FilterOutlined,
  EyeOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  CloseCircleOutlined,
  FileOutlined,
  CalendarOutlined,
  TeamOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { getUserTasks } from "@/api/adminApi";
import api from "@/api/api";
import "./AdminUserTasks.css";

const { Option } = Select;

// Helper function to format dates in human-readable format
const formatHumanDate = (dateString) => {
  if (!dateString) return "N/A";

  const date = new Date(dateString);
  const now = new Date();

  // Reset time part for accurate day comparison
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const diffMs = now - date;
  const diffDays = Math.floor((nowOnly - dateOnly) / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  const timeStr = date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });

  // If within the last minute
  if (diffMinutes < 1) {
    return "Hozir";
  }
  // If within the last hour
  if (diffMinutes < 60) {
    return `${diffMinutes} daqiqa oldin`;
  }
  // If today
  if (diffDays === 0) {
    return `Bugun, ${timeStr}`;
  }
  // If yesterday
  if (diffDays === 1) {
    return `Kecha, ${timeStr}`;
  }
  // If within the last week
  if (diffDays < 7) {
    return `${diffDays} kun oldin`;
  }

  // Format month names in Uzbek
  const months = [
    "yanvar", "fevral", "mart", "aprel", "may", "iyun",
    "iyul", "avgust", "sentyabr", "oktyabr", "noyabr", "dekabr"
  ];

  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  // If this year
  if (year === now.getFullYear()) {
    return `${day} ${month}`;
  }
  // Otherwise
  return `${day} ${month} ${year}`;
};

// Helper function to calculate deadline status
const getDeadlineInfo = (deadline) => {
  if (!deadline) return null;

  const now = new Date();
  const deadlineDate = new Date(deadline);
  const diffMs = deadlineDate - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));

  const isPast = diffMs < 0;
  const absDays = Math.abs(diffDays);
  const absHours = Math.abs(diffHours);

  let statusText = "";
  let statusColor = "";
  let icon = null;

  if (isPast) {
    if (absDays > 0) {
      statusText = `${absDays} kun kechiktirilgan`;
      statusColor = "#ff4d4f";
      icon = <WarningOutlined style={{ color: statusColor }} />;
    } else if (absHours > 0) {
      statusText = `${absHours} soat kechiktirilgan`;
      statusColor = "#ff4d4f";
      icon = <WarningOutlined style={{ color: statusColor }} />;
    } else {
      statusText = "Bugun muddati tugaydi";
      statusColor = "#faad14";
      icon = <ClockCircleOutlined style={{ color: statusColor }} />;
    }
  } else {
    if (diffDays > 3) {
      statusText = `${diffDays} kun qoldi`;
      statusColor = "#52c41a";
      icon = <CheckCircleOutlined style={{ color: statusColor }} />;
    } else if (diffDays > 1) {
      statusText = `${diffDays} kun qoldi`;
      statusColor = "#faad14";
      icon = <ClockCircleOutlined style={{ color: statusColor }} />;
    } else if (diffDays === 1) {
      statusText = "1 kun qoldi";
      statusColor = "#ff7a45";
      icon = <WarningOutlined style={{ color: statusColor }} />;
    } else if (diffHours > 0) {
      statusText = `${diffHours} soat qoldi`;
      statusColor = "#ff4d4f";
      icon = <WarningOutlined style={{ color: statusColor }} />;
    } else {
      statusText = "Muddati tugayapti";
      statusColor = "#ff4d4f";
      icon = <WarningOutlined style={{ color: statusColor }} />;
    }
  }

  return { statusText, statusColor, icon, isPast };
};

const AdminUserTasks = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // Modal states
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDetailLoading, setTaskDetailLoading] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    status: null,
    task_type: null,
    search: "",
  });

  useEffect(() => {
    fetchTasks();
  }, [userId, pagination.current, pagination.pageSize, filters]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        page_size: pagination.pageSize,
      };

      if (filters.status) params.status = filters.status;
      if (filters.task_type) params.task_type = filters.task_type;
      if (filters.search) params.search = filters.search;

      const data = await getUserTasks(userId, params);

      setTasks(data.items || []);
      setUserInfo(data.user);
      setPagination((prev) => ({
        ...prev,
        total: data.total,
      }));
    } catch (error) {
      console.error("Error fetching user tasks:", error);
      message.error(
        error.response?.data?.message || "Vazifalar yuklanmadi"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize,
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setPagination((prev) => ({ ...prev, current: 1 })); // Reset to first page
  };

  const handleReset = () => {
    setFilters({
      status: null,
      task_type: null,
      search: "",
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleViewTask = async (taskId) => {
    setIsModalVisible(true);
    setTaskDetailLoading(true);
    try {
      const response = await api.get(`/tasks/task/${taskId}`);
      setSelectedTask(response.data.data);
    } catch (error) {
      console.error("Error fetching task details:", error);
      message.error("Vazifa ma'lumotlari yuklanmadi");
    } finally {
      setTaskDetailLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedTask(null);
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      pending: { color: "default", icon: <ClockCircleOutlined />, text: "Kutilmoqda" },
      in_progress: { color: "processing", icon: <SyncOutlined spin />, text: "Jarayonda" },
      submitted: { color: "warning", icon: <FileOutlined />, text: "Tasdiqlanmoqda" },
      approved: { color: "success", icon: <CheckCircleOutlined />, text: "Tasdiqlangan" },
      rejected: { color: "error", icon: <CloseCircleOutlined />, text: "Rad etilgan" },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const getRelationBadges = (relations) => {
    if (!relations || relations.length === 0) return null;

    return (
      <Space size={4}>
        {relations.includes("created") && (
          <Tooltip title="Bu vazifani yaratgan">
            <Badge
              count="Yaratgan"
              style={{ backgroundColor: "#52c41a" }}
            />
          </Tooltip>
        )}
        {relations.includes("assigned") && (
          <Tooltip title="Bu vazifa unga tayinlangan">
            <Badge
              count="Tayinlangan"
              style={{ backgroundColor: "#1890ff" }}
            />
          </Tooltip>
        )}
      </Space>
    );
  };

  const columns = [
    {
      title: "Vazifa nomi",
      dataIndex: "title",
      key: "title",
      width: "30%",
      render: (text, record) => (
        <div>
          <a
            onClick={() => handleViewTask(record.id)}
            style={{ fontWeight: 500, color: "#1890ff", cursor: "pointer" }}
          >
            {text}
          </a>
          {record.description && (
            <div
              style={{
                fontSize: "12px",
                color: "#8c8c8c",
                marginTop: "4px",
              }}
            >
              {record.description.length > 60
                ? `${record.description.substring(0, 60)}...`
                : record.description}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "15%",
      render: (status) => getStatusTag(status),
    },
    {
      title: "Munosabat",
      dataIndex: "task_relation",
      key: "task_relation",
      width: "15%",
      render: (relations) => getRelationBadges(relations),
    },
    {
      title: "Yaratgan",
      dataIndex: "created_by",
      key: "created_by",
      width: "15%",
      render: (createdBy) => (
        <div>
          <div style={{ fontWeight: 500 }}>{createdBy?.name}</div>
          <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
            {createdBy?.employee_id}
          </div>
        </div>
      ),
    },
    {
      title: "Yaratilgan sana",
      dataIndex: "created_at",
      key: "created_at",
      width: "15%",
      render: (date) => new Date(date).toLocaleDateString("uz-UZ"),
    },
    {
      title: "Amallar",
      key: "actions",
      width: "10%",
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewTask(record.id)}
        >
          Ko'rish
        </Button>
      ),
    },
  ];

  const stats = [
    {
      title: "Jami vazifalar",
      value: pagination.total,
      icon: <FileOutlined />,
      color: "#1890ff",
    },
    {
      title: "Yaratgan vazifalar",
      value: tasks.filter((t) => t.task_relation?.includes("created")).length,
      icon: <CheckCircleOutlined />,
      color: "#52c41a",
    },
    {
      title: "Tayinlangan vazifalar",
      value: tasks.filter((t) => t.task_relation?.includes("assigned")).length,
      icon: <UserOutlined />,
      color: "#faad14",
    },
  ];

  return (
    <Container fluid className="p-4">
      {/* Breadcrumb */}
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/" }}>
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/users" }}>
          Foydalanuvchilar
        </Breadcrumb.Item>
        <Breadcrumb.Item active>
          {userInfo?.name || "User"}
        </Breadcrumb.Item>
      </Breadcrumb>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center my-4">
        <div>
          <h5 className="mb-1">
            <UserOutlined /> {userInfo?.name || "Foydalanuvchi"}
          </h5>
          <p className="text-muted mb-0">
            Foydalanuvhi ID: {userInfo?.employee_id}
          </p>
        </div>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchTasks}
          loading={loading}
        >
          Yangilash
        </Button>
      </div>

      {/* Statistics */}
      <Row  className="mb-4">
        {stats.map((stat, index) => (
          <Col key={index}  >
            <Card>
              <Statistic
                title={stat.title}
                value={stat.value}
                prefix={stat.icon}
                valueStyle={{ color: stat.color }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filters */}
      <BootstrapCard className="mb-4">
        <BootstrapCard.Body>
          <Row  >
            <Col  >
              <div className="mb-2">
                <small className="text-muted">Status bo'yicha filter</small>
              </div>
              <Select
                placeholder="Barcha statuslar"
                style={{ width: "100%" }}
                allowClear
                value={filters.status}
                onChange={(value) => handleFilterChange("status", value)}
              >
                <Option value="pending">Kutilmoqda</Option>
                <Option value="in_progress">Jarayonda</Option>
                <Option value="submitted">Tasdiqlanmoqda</Option>
                <Option value="approved">Tasdiqlangan</Option>
                <Option value="rejected">Rad etilgan</Option>
              </Select>
            </Col>

            <Col >
              <div className="mb-2">
                <small className="text-muted">Vazifa turi bo'yicha filter</small>
              </div>
              <Select
                placeholder="Barcha turlar"
                style={{ width: "100%" }}
                allowClear
                value={filters.task_type}
                onChange={(value) => handleFilterChange("task_type", value)}
              >
                <Option value="created">Faqat yaratgan vazifalar</Option>
                <Option value="assigned">Faqat tayinlangan vazifalar</Option>
              </Select>
            </Col>

            <Col  >
              <div className="mb-2">
                <small className="text-muted">Qidirish</small>
              </div>
              <Space.Compact style={{ width: "100%" }}>
                <Input
                  placeholder="Vazifa nomi yoki tavsifi..."
                  prefix={<SearchOutlined />}
                  value={filters.search}
                  onChange={(e) =>
                    handleFilterChange("search", e.target.value)
                  }
                  allowClear
                />
                <Button
                  type="default"
                  icon={<FilterOutlined />}
                  onClick={handleReset}
                >
                  Tozalash
                </Button>
              </Space.Compact>
            </Col>
          </Row>
        </BootstrapCard.Body>
      </BootstrapCard>

      {/* Tasks Table */}
      <BootstrapCard>
        <BootstrapCard.Body>
          <Spin spinning={loading}>
            {tasks.length > 0 ? (
              <>
                <Table
                  columns={columns}
                  dataSource={tasks}
                  rowKey="id"
                  pagination={false}
                  scroll={{ x: 800 }}
                />

                <div className="mt-4 d-flex justify-content-center">
                  <Pagination
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    onChange={handleTableChange}
                    showSizeChanger
                    showTotal={(total, range) =>
                      `${range[0]}-${range[1]} / ${total} ta vazifa`
                    }
                    pageSizeOptions={[10, 20, 50, 100]}
                  />
                </div>
              </>
            ) : (
              <Empty
                description="Vazifalar topilmadi"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Spin>
        </BootstrapCard.Body>
      </BootstrapCard>

      {/* Task Detail Modal */}
      <Modal
        title={null}
        open={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={900}
        centered
        bodyStyle={{ padding: 0 }}
        destroyOnClose
        closeIcon={
          <div
            style={{
              color: "white",
              fontSize: "20px",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.2)";
            }}
          >
            ✕
          </div>
        }
      >
        <Spin spinning={taskDetailLoading}>
          {selectedTask && (
            <div>
              {/* Modal Header - Minimalist */}
              <div
                style={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  padding: "32px 32px 24px 32px",
                  borderRadius: "8px 8px 0 0",
                  color: "white",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "16px" }}>
                  <h3 style={{ margin: 0, color: "white", fontSize: "22px", fontWeight: 500, flex: 1 }}>
                    {selectedTask.title}
                  </h3>
                  <div>{getStatusTag(selectedTask.status)}</div>
                </div>
              </div>

              {/* Modal Body */}
              <div style={{ padding: "24px 32px" }}>
                {/* Deadline Alert */}
                {selectedTask.deadline && (() => {
                  const deadlineInfo = getDeadlineInfo(selectedTask.deadline);
                  return (
                    <Alert
                      message={
                        <Space>
                          <CalendarOutlined />
                          <span style={{ fontWeight: 500 }}>
                            Muddat: {new Date(selectedTask.deadline).toLocaleDateString("uz-UZ", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit"
                            })}
                          </span>
                        </Space>
                      }
                      description={
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
                          {deadlineInfo.icon}
                          <span style={{ color: deadlineInfo.statusColor, fontWeight: 500, fontSize: "15px" }}>
                            {deadlineInfo.statusText}
                          </span>
                        </div>
                      }
                      type={deadlineInfo.isPast ? "error" : deadlineInfo.statusColor === "#52c41a" ? "success" : "warning"}
                      showIcon
                      style={{ marginBottom: "24px" }}
                    />
                  );
                })()}

                {/* Description Card */}
                {selectedTask.description && (
                  <Card size="small" style={{ marginBottom: "24px", backgroundColor: "#f8f9fa" }}>
                    <div style={{ fontSize: "13px", color: "#666", marginBottom: "8px", fontWeight: 500 }}>
                      📝 Tavsif
                    </div>
                    <div style={{ fontSize: "14px", lineHeight: "1.6", color: "#333" }}>
                      {selectedTask.description}
                    </div>
                  </Card>
                )}

                {/* Additional Info */}
                <Descriptions bordered column={2} size="small" style={{ marginBottom: "24px" }}>
                  <Descriptions.Item label="📅 Yaratilgan">
                    <Tooltip title={new Date(selectedTask.created_at).toLocaleString("uz-UZ")}>
                      <span style={{ fontWeight: 500 }}>
                        {formatHumanDate(selectedTask.created_at)}
                      </span>
                    </Tooltip>
                  </Descriptions.Item>

                  {selectedTask.priority && (
                    <Descriptions.Item label="⚡ Muhimlik">
                      <Tag
                        color={
                          selectedTask.priority === "high"
                            ? "red"
                            : selectedTask.priority === "medium"
                            ? "orange"
                            : "blue"
                        }
                        style={{ fontSize: "13px", padding: "4px 12px" }}
                      >
                        {selectedTask.priority === "high"
                          ? "Yuqori"
                          : selectedTask.priority === "medium"
                          ? "O'rta"
                          : "Past"}
                      </Tag>
                    </Descriptions.Item>
                  )}

                  {selectedTask.submitted_at && (
                    <Descriptions.Item label="📤 Topshirilgan" span={selectedTask.priority ? 2 : 1}>
                      <Tooltip title={new Date(selectedTask.submitted_at).toLocaleString("uz-UZ")}>
                        <span style={{ fontWeight: 500 }}>
                          {formatHumanDate(selectedTask.submitted_at)}
                        </span>
                      </Tooltip>
                    </Descriptions.Item>
                  )}
                </Descriptions>

                {/* Steps Timeline */}
                {selectedTask.steps && selectedTask.steps.length > 0 && (
                  <div style={{ marginBottom: "24px" }}>
                    <Divider orientation="left" style={{ fontSize: "15px", fontWeight: 600 }}>
                      ✅ Qadamlar ({selectedTask.steps.filter(s => s.is_completed).length}/{selectedTask.steps.length})
                    </Divider>
                    <Card size="small">
                      <Timeline
                        items={selectedTask.steps.map((step, index) => ({
                          color: step.is_completed ? "green" : "gray",
                          dot: step.is_completed ? (
                            <CheckCircleOutlined style={{ fontSize: "16px" }} />
                          ) : (
                            <ClockCircleOutlined style={{ fontSize: "16px" }} />
                          ),
                          children: (
                            <div>
                              <div style={{ fontWeight: 500, fontSize: "14px", marginBottom: "4px" }}>
                                {index + 1}. {step.title}
                              </div>
                              {step.created_at && (
                                <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
                                  {formatHumanDate(step.created_at)}
                                </div>
                              )}
                            </div>
                          ),
                        }))}
                      />
                    </Card>
                  </div>
                )}

                {/* Assigned Groups */}
                {selectedTask.assigned_groups && selectedTask.assigned_groups.length > 0 && (
                  <div style={{ marginBottom: "24px" }}>
                    <Divider orientation="left" style={{ fontSize: "15px", fontWeight: 600 }}>
                      👥 Tayinlangan guruhlar
                    </Divider>
                    <Card size="small">
                      <Space wrap>
                        {selectedTask.assigned_groups.map((group) => (
                          <Tag key={group.id} color="blue" style={{ fontSize: "13px", padding: "6px 12px" }}>
                            <TeamOutlined /> {group.name}
                          </Tag>
                        ))}
                      </Space>
                    </Card>
                  </div>
                )}

                {/* Approval Info */}
                {selectedTask.status === "approved" && selectedTask.approved_by_id && (
                  <div style={{ marginBottom: "24px" }}>
                    <Divider orientation="left" style={{ fontSize: "15px", fontWeight: 600 }}>
                      ✓ Tasdiqlash ma'lumotlari
                    </Divider>
                    <Card
                      size="small"
                      style={{
                        borderLeft: "4px solid #52c41a",
                        backgroundColor: "#f6ffed",
                      }}
                    >
                      <Descriptions bordered size="small" column={1}>
                        <Descriptions.Item label="Tasdiqlagan">
                          <Tag color="green">{selectedTask.approved_by_id}</Tag>
                        </Descriptions.Item>
                        {selectedTask.approved_at && (
                          <Descriptions.Item label="Tasdiqlangan sana">
                            <Tooltip title={new Date(selectedTask.approved_at).toLocaleString("uz-UZ")}>
                              <span style={{ fontWeight: 500 }}>
                                {formatHumanDate(selectedTask.approved_at)}
                              </span>
                            </Tooltip>
                          </Descriptions.Item>
                        )}
                      </Descriptions>
                    </Card>
                  </div>
                )}

                {/* Rejection Info */}
                {selectedTask.status === "rejected" && (
                  <div style={{ marginBottom: "24px" }}>
                    <Divider orientation="left" style={{ fontSize: "15px", fontWeight: 600 }}>
                      ❌ Rad etish ma'lumotlari
                    </Divider>
                    <Alert
                      message="Vazifa rad etildi"
                      description={
                        <div style={{ marginTop: "8px" }}>
                          <strong>Sabab:</strong>
                          <div style={{ marginTop: "4px", fontSize: "14px" }}>
                            {selectedTask.rejection_reason || "Sabab ko'rsatilmagan"}
                          </div>
                        </div>
                      }
                      type="error"
                      showIcon
                      icon={<CloseCircleOutlined />}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </Spin>
      </Modal>
    </Container>
  );
};

export default AdminUserTasks;
